// Las tareas del paso 09 que son iguales en contado y en crédito: instalar la app, entregar la
// tarjeta física y esperar a que el cliente termine su registro. Las dos pistas montan ESTE bloque.
import { QRCodeSVG } from 'qrcode.react';
import {
    AlertTriangle,
    Apple,
    CalendarClock,
    Check,
    Clock,
    CreditCard,
    Inbox,
    PlayCircle,
    QrCode,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { QueryErrorNotice, InlineQueryError } from '../QueryErrorNotice';
import { clientStepsSettled, SKIPPABLE_ONBOARDING_STEP } from './deliveryGate';
import { TaskItem, type TaskState } from './TaskItem';
import {
    ACCOUNT_OPENING_CARD_DEFERRED,
    ACCOUNT_OPENING_CARD_UNDER_REVIEW,
    ACCOUNT_OPENING_NOT_BLOCKING,
} from '../../accountOpening';
import type { DeliveryTasksState } from './useDeliveryTasks';
import type { AppOnboardingStep, AppOnboardingStatus, AppOnboardingStepState } from '../../types';

const ONBOARDING_LABELS: Record<AppOnboardingStep, string> = {
    PASSWORD: 'Creó su contraseña',
    CONTRACT_SIGNED: 'Firmó su contrato',
    SECRET_NUMBER: 'Registró su número secreto',
    PHYSICAL_CARD: 'Activó su tarjeta física',
};

/**
 * Lo que la fila puede mostrar. `DEFERRED` no existe en el silo: lo pone el escape del vendedor, que
 * es decisión suya y no una señal del backend.
 */
type OnboardingRowStatus = AppOnboardingStatus | 'DEFERRED';

interface OnboardingRow {
    step: string;
    status: OnboardingRowStatus;
    reason?: string;
}

/** «No verificado» ≠ «Pendiente»: uno es una lectura nuestra caída, el otro es el cliente. */
const ONBOARDING_STATUS_LABELS: Record<OnboardingRowStatus, string> = {
    COMPLETED: 'Listo',
    PENDING: 'Pendiente',
    UNVERIFIABLE: 'No verificado',
    DEFERRED: 'Para después',
};

/** Una condición que el silo agregue después se pinta con su clave, nunca con una fila en blanco. */
function onboardingLabel(step: string): string {
    return (ONBOARDING_LABELS as Record<string, string>)[step] ?? step;
}

/** Un status fuera del catálogo se anuncia como no verificable, jamás con el chip en blanco. */
function onboardingStatusLabel(status: OnboardingRowStatus): string {
    return (
        (ONBOARDING_STATUS_LABELS as Record<string, string>)[status] ??
        ONBOARDING_STATUS_LABELS.UNVERIFIABLE
    );
}

/** Copy de la entrega con teléfono nuevo o, en una apertura, sobre el celular que el cliente ya trae. */
interface HandoverCopy {
    appTitle: string;
    appDesc: string;
    openCamera: string;
    /** Lo que se le entrega al cliente: «el equipo» o «sus productos». */
    handOver: string;
    handOverAnyway: string;
}

const PHONE_COPY: HandoverCopy = {
    appTitle: 'Instalar la app Fiado en el equipo nuevo',
    appDesc:
        'El cliente escanea el QR con la cámara del equipo. La app se abre ya con su cuenta PagoConFiado lista.',
    openCamera: 'Abrir la cámara del equipo nuevo.',
    handOver: 'Entrégale el equipo',
    handOverAnyway: 'entrega el equipo igual',
};

const OWN_DEVICE_COPY: HandoverCopy = {
    appTitle: 'Instalar la app Fiado en el celular del cliente',
    appDesc:
        'El cliente escanea el QR con la cámara de su celular. La app se abre ya con su cuenta PagoConFiado lista.',
    openCamera: 'Abrir la cámara de su celular.',
    handOver: 'Entrégale sus productos',
    handOverAnyway: 'entrega los productos igual',
};

export function DeliveryTasks({
    tasks,
    firstNumber = 1,
    blocked = false,
    accountOpening = false,
}: {
    tasks: DeliveryTasksState;
    /** Número de la primera tarea: en crédito viene después del IMEI y del candado remoto. */
    firstNumber?: number;
    /** La pista todavía no llegó hasta aquí (en crédito, sin el crédito activo). */
    blocked?: boolean;
    /** Apertura de cuenta: no hay venta ni productos que entregar. */
    accountOpening?: boolean;
}) {
    const copy = accountOpening ? OWN_DEVICE_COPY : PHONE_COPY;
    const state = (done: boolean): TaskState => (blocked ? 'blocked' : done ? 'done' : 'active');

    // La tarjeta se entrega «a la par» del registro sin este gate: bloqueada hasta que el cliente
    // cierre su cuenta (contraseña, contrato, PIN), sin esperar a que active el plástico que aún no le
    // dimos. Ya resuelta (entregada, saltada, o sin nada que entregar) el gate no tiene nada que trabar.
    const cardBlocked = blocked || (!tasks.cardResolved && !clientStepsSettled(tasks.steps));
    const cardState: TaskState = cardBlocked ? 'blocked' : tasks.cardResolved ? 'done' : 'active';

    return (
        <>
            <AppInstallTask
                n={firstNumber}
                state={state(tasks.appReached)}
                tasks={tasks}
                copy={copy}
            />
            <AppOnboardingTask
                n={firstNumber + 1}
                state={state(tasks.onboardingSettled)}
                tasks={tasks}
                accountOpening={accountOpening}
            />
            {!tasks.cardTaskHidden && (
                <CardDeliveryTask
                    n={firstNumber + 2}
                    state={cardState}
                    tasks={tasks}
                    copy={copy}
                    accountOpening={accountOpening}
                />
            )}
        </>
    );
}

/** Tarea de la app: el QR que el cliente escanea con la cámara de su teléfono. */
function AppInstallTask({
    n,
    state,
    tasks,
    copy,
}: {
    n: number;
    state: TaskState;
    tasks: DeliveryTasksState;
    copy: HandoverCopy;
}) {
    return (
        <TaskItem
            n={n}
            state={state}
            title={copy.appTitle}
            desc={
                tasks.linkError
                    ? 'No hay QR que escanear: el enlace no se pudo generar.'
                    : copy.appDesc
            }
        >
            <div className="flex flex-col items-start gap-3 sm:flex-row sm:gap-4">
                <div className="flex flex-none flex-col items-start gap-2">
                    <div className="flex size-[120px] items-center justify-center rounded-lg border border-border bg-white p-2 text-muted-foreground">
                        {tasks.installLink ? (
                            <QRCodeSVG
                                value={tasks.installLink}
                                size={104}
                                role="img"
                                aria-label="Código QR para instalar la app Fiado"
                            />
                        ) : (
                            // Sin enlace NO se pinta un QR falso: un cuadro que no lleva a ningún lado
                            // se escanea igual y manda al cliente a la nada.
                            <QrCode className="size-16 opacity-40" aria-hidden />
                        )}
                    </div>
                    <StoreBadges />
                </div>
                <div className="min-w-0 flex-1">
                    {/* Sin enlace no hay nada que escanear, así que los pasos del cliente sobran: lo
                        que hace falta es el motivo y por dónde salir. */}
                    <QueryErrorNotice
                        className="mx-0 mb-3 max-w-none"
                        title="No se pudo generar el enlace de instalación"
                        error={tasks.linkError}
                        fallback="No se pudo pedir el enlace de la app."
                        hint="Sin QR el cliente no puede instalarla aquí."
                        onRetry={tasks.retryLink}
                        retrying={tasks.linkFetching}
                    />
                    {!tasks.linkError && (
                        <div className="mb-3 text-[12.5px] leading-relaxed">
                            <strong className="font-semibold">Pasos para el cliente:</strong>
                            <ol className="ml-4 mt-2 list-decimal space-y-1 text-[12px]">
                                <li>{copy.openCamera}</li>
                                <li>Enfocar el QR de esta pantalla.</li>
                                <li>Descargar la app cuando se lo pida.</li>
                                <li>Al abrir la app se conecta con su cuenta sola.</li>
                            </ol>
                        </div>
                    )}
                </div>
            </div>
        </TaskItem>
    );
}

/** Sin deep link real todavía: son placeholders hasta que el negocio defina la liga de cada tienda. */
function StoreBadges() {
    const badge =
        'flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-[10.5px] font-medium text-muted-foreground transition-colors hover:text-foreground';
    return (
        <div className="flex flex-col gap-1.5">
            <button type="button" className={badge}>
                <Apple className="size-3.5 flex-none" aria-hidden />
                Descárgala en App Store
            </button>
            <button type="button" className={badge}>
                <PlayCircle className="size-3.5 flex-none" aria-hidden />
                Disponible en Google Play
            </button>
        </div>
    );
}

/** Tarea de la tarjeta: se cierra sola cuando el cliente la activa, y ahí se descuenta del inventario. */
function CardDeliveryTask({
    n,
    state,
    tasks,
    copy,
    accountOpening,
}: {
    n: number;
    state: TaskState;
    tasks: DeliveryTasksState;
    copy: HandoverCopy;
    accountOpening: boolean;
}) {
    // El único resuelto que deja algo por hacer en otra visita. Plegado a una línea no dice dónde
    // quedó esa tarjeta ni deja volver atrás al vendedor que lo picó de más.
    const deferredForStock = tasks.cardSkipped && !tasks.cardDelivered;
    const cardStep = tasks.steps.find((s) => s.step === SKIPPABLE_ONBOARDING_STEP);
    // Venta con la entrega ya registrada y sin activar: el renglón dice en qué quedó.
    const awaitingActivation =
        tasks.cardDelivered && cardStep !== undefined && cardStep.status !== 'COMPLETED';
    // Una activación que no pudimos leer se queda a la vista: es lo que el vendedor tiene que ver.
    const activationUnverified = cardStep?.status === 'UNVERIFIABLE';

    return (
        <TaskItem
            n={n}
            state={state}
            // Resuelta la tarea, el cuerpo se pliega y se lleva el escape. Mientras la condición de la
            // tarjeta trabe el gate, esa es la única llave del paso y no puede salir de pantalla.
            alwaysOpen={
                tasks.cardEscapeVisible ||
                deferredForStock ||
                awaitingActivation ||
                activationUnverified ||
                !!tasks.deliveryError
            }
            title="Entregar la tarjeta PagoConFiado"
            desc={
                accountOpening && tasks.cardDeferredByAccount && !tasks.cardDelivered
                    ? ACCOUNT_OPENING_CARD_UNDER_REVIEW
                    : cardTaskDesc(tasks, copy)
            }
        >
            <div className="flex flex-col gap-3">
                {/* La activa el cliente DESPUÉS, sobre la tarjeta que este sub-paso entrega. */}
                {cardStep && (
                    <ul aria-label="Activación de la tarjeta" className="flex flex-col gap-1.5">
                        <OnboardingCheck
                            {...onboardingRow(cardStep, tasks)}
                            accountOpening={accountOpening}
                        />
                    </ul>
                )}
                {deferredForStock ? (
                    <CardDeferredBody tasks={tasks} copy={copy} accountOpening={accountOpening} />
                ) : (
                    <CardHandoverBody tasks={tasks} />
                )}
            </div>
        </TaskItem>
    );
}

/**
 * El mostrador del sub-paso: qué es el plástico, cómo lo activa el cliente después y la advertencia.
 * El cuerpo no espera a nada — la tarjeta ya está en el mostrador, así que entregarla no depende de
 * que la cuenta PagoConFiado termine de abrir.
 */
function CardHandoverBody({ tasks }: { tasks: DeliveryTasksState }) {
    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-col items-start gap-3 sm:flex-row sm:gap-4">
                <CardArtwork />
                <div className="min-w-0 flex-1 text-[12.5px] leading-relaxed">
                    <strong className="font-semibold">
                        Pasos para el cliente en su app Fiado:
                    </strong>
                    <ol
                        aria-label="Pasos para activar la tarjeta en la app del cliente"
                        className="ml-4 mt-2 list-decimal space-y-1 text-[12px]"
                    >
                        <li>Abre la app Fiado y toca tu tarjeta.</li>
                        <li>Toca «Continuar» y firma el contrato de la tarjeta.</li>
                        <li>Toca «¿Ya recibiste tu tarjeta? Actívala».</li>
                        <li>Escribe los 16 dígitos de tu tarjeta.</li>
                    </ol>
                </div>
            </div>
            {tasks.deliveryError && (
                <InlineQueryError
                    className="text-[12px]"
                    message={tasks.deliveryError}
                    onRetry={tasks.deliverCard}
                    retrying={tasks.delivering}
                />
            )}

            {/* Sin botón de entrega: la activación del cliente cierra la tarea y descuenta la tarjeta.
                El escape solo aparece cuando el silo no supo si la tienda tiene inventario. */}
            {tasks.cardEscapeVisible && (
                <div className="flex">
                    <button
                        type="button"
                        disabled={tasks.delivering}
                        onClick={tasks.skipCard}
                        className="text-left text-[11px] text-muted-foreground underline decoration-dotted hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
                    >
                        {cardEscapeLabel(tasks)}
                    </button>
                </div>
            )}
        </div>
    );
}

/** Lo que queda del sub-paso cuando la tienda se quedó sin plásticos: dónde queda esa tarjeta. */
function CardDeferredBody({
    tasks,
    copy,
    accountOpening,
}: {
    tasks: DeliveryTasksState;
    copy: HandoverCopy;
    accountOpening: boolean;
}) {
    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-start gap-2 rounded-md border border-info-soft-border bg-info-soft px-3 py-2 text-[11.5px] leading-relaxed text-info">
                <Inbox className="mt-0.5 size-4 flex-none" aria-hidden />
                {/* La descripción de la tarea ya dice DÓNDE quedó: aquí va lo que el vendedor
                    todavía no sabe — que no se consumió nada y que puede seguir. */}
                {accountOpening ? (
                    <span>{ACCOUNT_OPENING_CARD_DEFERRED}</span>
                ) : (
                    <span>
                        No se descontó ninguna tarjeta del inventario y la venta puede continuar.
                        {copy.handOver}; el plástico se lo das en la próxima visita.
                    </span>
                )}
            </div>
            {/* El escape es local y no tocó el inventario, así que un clic de más se deshace aquí:
                sin esto, la venta se cierra sin tarjeta que sí había. */}
            <div className="flex">
                <button
                    type="button"
                    onClick={tasks.undoSkipCard}
                    className="text-left text-[11px] text-muted-foreground underline decoration-dotted hover:text-foreground"
                >
                    Deshacer — sí hay tarjeta para entregarle
                </button>
            </div>
        </div>
    );
}

/** El plástico, del mismo tamaño que el QR de arriba: sin él el sub-paso queda sin ancla visual. */
function CardArtwork() {
    return (
        <div
            aria-hidden
            className="flex size-[120px] flex-none flex-col justify-between rounded-lg border border-border bg-accent/60 p-3"
        >
            <CreditCard className="size-5 text-primary" />
            <div className="min-w-0">
                <div className="text-[9.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                    Tarjeta física
                </div>
                <div className="truncate text-[12px] font-semibold leading-tight">PagoConFiado</div>
            </div>
        </div>
    );
}

// Una venta vieja con el plástico ya registrado y sin activar: ahí la salida no es la falta de stock.
function cardEscapeLabel(tasks: DeliveryTasksState): string {
    return tasks.cardDelivered
        ? 'El cliente no puede activarla ahora — continuar'
        : 'Sin tarjeta en inventario — continuar';
}

// El motivo va en la descripción y no en el cuerpo: la tarea resuelta se pliega, y ahí es justo
// cuando el vendedor necesita leer por qué no entregó nada.
function cardTaskDesc(tasks: DeliveryTasksState, copy: HandoverCopy): string {
    // La cuenta puede caer a revisión DESPUÉS de la entrega, y el plástico ya salió del mostrador.
    if (tasks.cardDeferredByAccount && !tasks.cardDelivered) {
        return `La cuenta quedó pendiente de revisión: ${copy.handOverAnyway}, la tarjeta va a tu bandeja de Pendientes.`;
    }
    if (tasks.cardAlreadyOwned && !tasks.cardDelivered) {
        return 'El cliente ya trae su tarjeta PagoConFiado vigente: no hay plástico que entregarle.';
    }
    // La entrega ocurrida manda sobre todo lo demás: con el plástico ya entregado, la falta de stock
    // y la bandeja de Pendientes serían las dos falsas.
    if (tasks.cardActivated) return 'El cliente ya activó su tarjeta.';
    if (tasks.cardDelivered) {
        return 'Tarjeta entregada. La activa el cliente desde su app.';
    }
    if (tasks.cardSkipped) {
        return 'Sin tarjeta en inventario. Queda en tu bandeja de Pendientes para otra visita.';
    }
    return 'Entrega la tarjeta física al cliente. La tarea se cierra sola cuando él la active en su app.';
}

/**
 * Tarea del registro que el cliente hace en su app: el vendedor no declara nada, solo lee. Queda a la
 * vista ya cumplida, que es cuando hay que leer si una condición no se pudo verificar.
 */
function AppOnboardingTask({
    n,
    state,
    tasks,
    accountOpening,
}: {
    n: number;
    state: TaskState;
    tasks: DeliveryTasksState;
    accountOpening: boolean;
}) {
    // La condición de la tarjeta se lee en el sub-paso que la entrega: aquí solo va lo que el cliente
    // cumple por su cuenta desde la app.
    const clientSteps = tasks.steps.filter((s) => s.step !== SKIPPABLE_ONBOARDING_STEP);

    return (
        <TaskItem
            n={n}
            state={state}
            alwaysOpen
            title="El cliente termina su registro en la app"
            desc="Las condiciones se prenden solas conforme él avanza. No hay nada que confirmar aquí."
        >
            <div className="flex flex-col gap-3">
                <QueryErrorNotice
                    className="mx-0 mb-0 max-w-none"
                    title="No se pudo leer el registro en la app"
                    error={tasks.onboardingError}
                    fallback="No se pudo consultar cómo va el registro del cliente."
                    hint="Sin esta lectura no sabemos qué le falta, así que el paso no avanza."
                    onRetry={tasks.retryOnboarding}
                    retrying={tasks.retryingOnboarding}
                />

                {clientSteps.length > 0 && (
                    <ul
                        aria-label="Condiciones del registro en la app"
                        className="flex flex-col gap-1.5"
                    >
                        {clientSteps.map((s) => (
                            <OnboardingCheck
                                key={s.step}
                                {...onboardingRow(s, tasks)}
                                accountOpening={accountOpening}
                            />
                        ))}
                    </ul>
                )}

                {!tasks.onboardingError && clientSteps.length === 0 && (
                    <p className="text-[12px] text-muted-foreground">
                        Esperando a que el cliente entre a su app…
                    </p>
                )}
            </div>
        </TaskItem>
    );
}

/**
 * El escape del vendedor NO viaja al silo: la condición de la tarjeta sigue llegando `PENDING`. La
 * traduce la pantalla, porque si no dice dos cosas del mismo hecho — el sub-paso en LISTO y su
 * condición en PENDIENTE. Aplazada no es cumplida: nadie activó ninguna tarjeta.
 */
function onboardingRow(s: AppOnboardingStepState, tasks: DeliveryTasksState): OnboardingRow {
    const deferrable = s.step === SKIPPABLE_ONBOARDING_STEP && s.status === 'PENDING';
    if (!deferrable || !tasks.cardSkipped) return s;
    return {
        step: s.step,
        status: 'DEFERRED',
        // Dónde quedó la tarjeta lo dicen el desc y el recuadro: aquí va lo que le falta al cliente
        // para poder activarla, que es distinto según haya salido o no el plástico.
        reason: tasks.cardDelivered
            ? 'Ya se la entregaste: la activa él desde su app cuando pueda.'
            : 'Todavía no tiene el plástico que activar.',
    };
}

/**
 * Una condición del registro. Todo lo que no sea «listo» ni «pendiente» va en ÁMBAR y con su motivo:
 * en gris el vendedor lee "el cliente no lo ha hecho" sobre una lectura NUESTRA que se cayó.
 */
function OnboardingCheck({
    step,
    status,
    reason,
    accountOpening = false,
}: OnboardingRow & { accountOpening?: boolean }) {
    const done = status === 'COMPLETED';
    const pending = status === 'PENDING';
    const deferred = status === 'DEFERRED';
    const unverifiable = !done && !pending && !deferred;
    const icon = done ? (
        <Check className="mt-0.5 size-3.5 flex-none" strokeWidth={3} aria-hidden />
    ) : pending ? (
        <Clock className="mt-0.5 size-3.5 flex-none" aria-hidden />
    ) : deferred ? (
        <CalendarClock className="mt-0.5 size-3.5 flex-none" aria-hidden />
    ) : (
        <AlertTriangle className="mt-0.5 size-3.5 flex-none" aria-hidden />
    );

    return (
        <li
            className={cn(
                'flex items-start gap-2 rounded-md border px-3 py-2 text-[12px] leading-relaxed',
                done && 'border-success-soft-border bg-success-soft/50 text-success',
                pending && 'border-border text-muted-foreground',
                deferred && 'border-info-soft-border bg-info-soft text-info',
                unverifiable && 'border-warning-soft-border bg-warning-soft text-warning',
            )}
        >
            {icon}
            <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                    <span className="font-semibold text-foreground">{onboardingLabel(step)}</span>
                    <span className="flex-none text-[10.5px] font-semibold uppercase tracking-[0.04em]">
                        {onboardingStatusLabel(status)}
                    </span>
                </div>
                {(unverifiable || deferred) && (
                    <p className="mt-0.5 text-[11.5px]">
                        {reason ?? 'No pudimos consultarlo.'}{' '}
                        {accountOpening ? ACCOUNT_OPENING_NOT_BLOCKING : 'No detiene la venta.'}
                    </p>
                )}
            </div>
        </li>
    );
}
